import React, { useMemo, useState } from 'react';
import { HiOutlineEye, HiOutlineExclamation } from 'react-icons/hi';
import { findInCatalog } from '../../../../../../utils/providerCatalog';
import { renderableContent } from '../studioDraft';
import { MessageCompositionStudio } from '../useMessageCompositionStudio';
import SelectTextField from '../../../../../molecules/SelectTextField';
import EmailPreview from './EmailPreview';
import SlackPreview from './SlackPreview';
import TestValues from './TestValues';
import WhatsAppPreview from './WhatsAppPreview';
import { DraftPreviewFragment, useDraftPreview } from './useDraftPreview';

const ENV_LABELS: Record<string, string> = { prd: 'Produção', hml: 'Homologação', dev: 'Desenvolvimento', sandbox: 'Sandbox' };

const PreviewPanel: React.FC<{ studio: MessageCompositionStudio }> = ({ studio }) => {
    const { meta, activeTemplate, activeBody, envelopeTemplate, previewValues, previewVariables } = studio;
    const [env, setEnv] = useState('prd');

    const fragments = useMemo<DraftPreviewFragment[]>(() => {
        if (!meta || !activeTemplate) return [];
        const config = meta.channelConfig;
        if (config.kind === 'EMAIL') {
            const envelope = envelopeTemplate?.envelope;
            const list: DraftPreviewFragment[] = [];
            if (activeBody) list.push({ key: 'body', content: renderableContent(activeBody) });
            if (envelope) {
                list.push({ key: 'subject', content: envelope.subject ?? '' });
                list.push({ key: 'preheader', content: envelope.preheader ?? '' });
                list.push({ key: 'from', content: envelope.from[0] ?? '' });
                list.push({ key: 'to', content: (envelope.to ?? []).join('\n') });
                (envelope.attachments ?? []).forEach((a, i) => list.push({ key: `att-${i}`, content: a.fileName || a.source }));
            }
            return list;
        }
        if (config.kind === 'INTERNAL_MESSAGE') return [{ key: 'message', content: activeTemplate.content }];
        return [];
    }, [meta, activeTemplate, activeBody, envelopeTemplate]);

    const { rendered, error } = useDraftPreview(fragments, previewValues, env);

    const content = useMemo(() => {
        if (!meta || !activeTemplate) return null;
        const config = meta.channelConfig;
        if (config.kind === 'EMAIL') {
            const envelope = envelopeTemplate?.envelope;
            const attachments = (envelope?.attachments ?? []).map((a, i) => ({
                name: rendered[`att-${i}`] ?? a.fileName ?? a.source,
                delivery: a.delivery,
                expirationMinutes: a.expirationMinutes,
            }));
            return (
                <EmailPreview
                    from={rendered.from ?? ''}
                    to={rendered.to ? rendered.to.split('\n').join(', ') : ''}
                    subject={rendered.subject ?? ''}
                    preheader={rendered.preheader ?? ''}
                    html={rendered.body ?? ''}
                    attachments={attachments}
                />
            );
        }
        if (config.kind === 'INTERNAL_MESSAGE') {
            const variant = config.variants.find((v) => v.environments.includes(env)) ?? config.variants[0];
            return <SlackPreview channel={variant?.channel ?? ''} text={rendered.message ?? ''} />;
        }
        const bindings = Object.fromEntries((activeTemplate.providerBindings ?? []).map((b) => [b.placeholder, b.variable]));
        return (
            <WhatsAppPreview
                provider={findInCatalog(studio.providerCatalog, activeTemplate.providerTemplateName)}
                values={previewValues}
                bindings={bindings}
                to={config.kind === 'EXTERNAL_MESSAGE' ? config.to[0] ?? '' : ''}
            />
        );
    }, [meta, activeTemplate, envelopeTemplate, rendered, previewValues, env]);

    if (!meta || !content) return null;
    const slackEnvs =
        meta.channelConfig.kind === 'INTERNAL_MESSAGE'
            ? Array.from(new Set(meta.channelConfig.variants.flatMap((v) => v.environments)))
            : Object.keys(ENV_LABELS);

    return (
        <div className="flex flex-col gap-3">
            <div className="flex items-end gap-2">
                <HiOutlineEye className="text-gray-400" size={18} />
                <span className="text-sm font-semibold text-gray-700 dark:text-gray-200 flex-1">Pré-visualização</span>
                {slackEnvs.length > 1 && (
                    <SelectTextField
                        id="preview-environment"
                        label="Ambiente"
                        value={env}
                        hideEmptyOption
                        className="w-40"
                        items={slackEnvs.map((e) => ({ value: e, label: ENV_LABELS[e] ?? e }))}
                        onChange={(e) => setEnv(e.target.value)}
                    />
                )}
            </div>
            {error && (
                <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">
                    <HiOutlineExclamation size={16} className="shrink-0 mt-0.5" />
                    <span>A lógica do modelo tem um erro: {error}</span>
                </div>
            )}
            {content}
            <TestValues
                variables={previewVariables}
                values={previewValues}
                onChange={(name, value) => studio.setTestValues({ ...studio.testValues, [name]: value })}
                onReset={() => studio.setTestValues({})}
            />
        </div>
    );
};

export default PreviewPanel;
